import { AdUnit, buildAdUnitsExportCSV } from '@pubstack/common/src/adunit'
import { useQueryClient } from '@tanstack/react-query'
import { parseAsInteger, parseAsNumberLiteral, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdUnits } from '~/api/adm-api.hook'
import { AD_UNITS_QUERY_KEYS, fetchAdUnitsForExport, useAdUnitsWithPagination, useSitesStacksByAdUnits } from '~/api/adm/ad-units.api'
import { PAGE_LIMIT_OPTIONS } from '~/components/Pagination'
import { useToast } from '~/components/Toast/useToasts'
import { downloadCSVData } from '~/utils/fileDownload'
import { PureAdmAdUnitsPage } from './PureAdmAdUnitsPage'

const ITEMS_PER_PAGE = 25

export const AdmAdUnitsPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const { byId } = useAdUnits(null)
  const { data: sitesStacks } = useSitesStacksByAdUnits()
  const toast = useToast()
  const queryClient = useQueryClient()

  // The page window, the search and the ordering live in the URL, so a list someone is looking at is a link
  // they can send — and so the reader comes back to the same page after archiving a row.
  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(ITEMS_PER_PAGE).withOptions({ clearOnDefault: false }))
  const [search, setSearch] = useQueryState<string>('search', parseAsString.withDefault(''))
  const [sortBy, setSortBy] = useQueryState<string>(
    'sortBy',
    parseAsStringLiteral(['name', 'divId'] as readonly string[])
      .withDefault('name')
      .withOptions({ clearOnDefault: false })
  )
  const [sortOrder, setSortOrder] = useQueryState<'ascending' | 'descending'>(
    'sortOrder',
    parseAsStringLiteral(['ascending', 'descending']).withDefault('ascending').withOptions({ clearOnDefault: false })
  )

  const {
    data: { data: adUnits, pagination },
    isLoading,
  } = useAdUnitsWithPagination({
    // The archived ones live on their own page, so this list asks for the live ones and never has to filter
    // them back out.
    filters: { search: search || undefined },
    pagination: { page, limit },
    sortBy,
    sortOrder,
  })

  // Archiving only: the way back is on the archived page, which writes through its own react-query mutation.
  const archiveAdUnit = async (adUnit: AdUnit) => {
    try {
      await byId.put({ ...adUnit, enabled: false })
      toast.success(`The ad unit ${adUnit.name} has been archived successfully.`)
      // The list is a server-side page now, so it is refetched rather than re-derived from a local array.
      await queryClient.invalidateQueries({ queryKey: AD_UNITS_QUERY_KEYS })
    } catch (_e) {
      toast.alert('An error occurred while trying to archive the ad unit.')
    }
  }

  /* Every ad unit the filters select, not the page on screen: exporting what the table happens to be showing
     would hand back twenty-five rows of a thousand. */
  const exportAdUnits = async () => {
    const rows = await fetchAdUnitsForExport({ search: search || undefined }, sortBy, sortOrder)
    downloadCSVData(buildAdUnitsExportCSV(rows), 'adUnits-export')
  }

  return (
    <PureAdmAdUnitsPage
      adUnits={adUnits}
      sitesStacks={sitesStacks}
      isLoading={isLoading}
      search={search}
      onSearchChange={(value) => {
        // Typing moves the reader back to the first page: page 4 of the previous search is not a place they
        // asked to be.
        setSearch(value)
        setPage(1)
      }}
      page={page}
      limit={limit}
      totalItems={pagination.total}
      totalPages={pagination.totalPages}
      onPageChange={(p) => setPage(p)}
      onLimitChange={(l) => {
        setLimit(l)
        setPage(1)
      }}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={(nextSortBy, nextSortOrder) => {
        setSortBy(nextSortBy)
        setSortOrder(nextSortOrder)
        setPage(1)
      }}
      onCreateAdUnit={() => navigate(`/monetization/one/adunits/new`)}
      onUpdateAdUnit={(adUnitId) => navigate(`/monetization/one/adunits/edit/${adUnitId}`)}
      onArchiveAdUnit={(adUnit) => archiveAdUnit(adUnit)}
      onExportAdUnits={exportAdUnits}
      onImportAdUnits={() => navigate('/monetization/one/adunits/import')}
      onNavigateToArchived={() => navigate('/monetization/inventory/adunits/web/archived')}
      onOpenFlyout={() => {}}
    />
  )
}
